<template>
    <div
        :class="[
            $styleCrit.game_area,
            props.showGameHeader && $styleCrit.game_area_with_header,
            isIOSSafari && $styleCrit.ios_safari,
        ]"
    >
        <template v-if="props.showGameHeader">
            <HorizontalGameExitHeader
                :class="$styleCrit.game_header_horizontal_override"
                :title="props.game.title"
                :isAdFreeActive="adDisabledStore.isAdFreeActive"
                :hideBranding="props.hideBranding"
                @closeGame="emit('closeGame')"
                @noAdsClick="emit('noAdsClick')"
                @click="focusGame"
            />
            <VerticalGameExitHeader
                :class="$styleCrit.game_header_vertical_override"
                :hideBranding="props.hideBranding"
                @closeGame="emit('closeGame')"
            />
        </template>
        <div :class="$styleCrit.iframe_wrapper">
            <iframe
                v-if="isGameFrameVisible"
                ref="game-iframe"
                :allow="iframeAllowAttribute"
                :class="$styleCrit.iframe"
                :title="props.game.title"
                :src="gameSrc"
            />
            <div
                v-if="!isGameFrameVisible"
                :class="$styleCrit.iframe_fallback"
            />
            <AdvancedBanners
                v-if="advancedBanners.length"
                :banners="advancedBanners"
                :isFullscreen="props.isFullscreen"
                :sidebarShown="props.sidebarShown"
            />
            <DisabledAdFiller
                v-if="isDisabledAdFillerVisible"
                :adLoader="adLoader"
                :keepSpaceForInstreamInfo="adInfo.isVisible"
                @message="disabledAdFillerHandler"
            />
        </div>
        <LoginPrompt
            v-if="props.shouldShowLoginPrompt"
            @close="emit('loginPromptClose')"
            @login="emit('loginPromptLogin')"
        />
        <ActionNotifier
            v-else-if="notifierQueue.length"
            :queue="notifierQueue"
            @click="focusGame"
        />
        <AdFlipCards
            v-if="props.showMobileInterface"
            :widgetMode="props.widgetMode"
            :class="[$styleCrit.ad_block_override, props.widgetMode && $styleCrit.ad_block_widget_override]"
        >
            <template #front>
                <StickyGameBanner
                    v-if="!props.widgetMode"
                    :game="props.game"
                    :categories="props.categories"
                    :hideCategory="appStore.clid === OPPO_WL_CLID"
                />
                <StickyWidgetBanner v-else />
            </template>
        </AdFlipCards>
        <ErrorAdFiller
            v-if="brandPreloaderHasError"
            :placement="brandPreloaderAdType"
            @close="onErrorAdFillerClose"
        />
        <AdBlockerOffer :openDisabledAdModal="props.openDisabledAdModal" />
        <StorageDebugMessage :saveEvent="lastSaveEvent" />
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, useTemplateRef } from 'vue'

import { OPPO_WL_CLID } from '@/constants/general'
import type { ChargeResponse, DisableAdOrder } from '@/modules/in-game-payments'
import { useAdDisabledStore } from '@/store/ad-disabled-store'
import { useAppStore } from '@/store/app-store'
import { useLayoutStore } from '@/store/layout-store'
import type { APIGameType, GameCategoriesInfoType } from '@/types'

import type { GameFrame } from '../composables/use-game-frame'
import { iframeAllowAttribute } from '../utils'
import ActionNotifier, { type NotifierItem, type Orientation } from './ActionNotifier/ActionNotifier.vue'
import AdBlockerOffer from './AdBlockerOffer/AdBlockerOffer.vue'
import AdFlipCards from './AdFlipCards.vue'
import AdvancedBanners from './AdvancedBanners/AdvancedBanners.vue'
import DisabledAdFiller from './DisabledAdFiller/DisabledAdFiller.vue'
import ErrorAdFiller from './ErrorAdFiller/ErrorAdFiller.vue'
import StorageDebugMessage from './GameContainer/StorageDebugMessage.vue'
import HorizontalGameExitHeader from './HorizontalGameExitHeader.vue'
import LoginPrompt from './LoginPrompt.vue'
import StickyGameBanner from './StickyGameBanner.vue'
import StickyWidgetBanner from './StickyWidgetBanner.vue'
import VerticalGameExitHeader from './VerticalGameExitHeader/VerticalGameExitHeader.vue'

// The game screen itself: init screen, the game iframe and every overlay around it.
// Shared by the export page (ExportGame.vue) and the direct game frame on the portal
// (GameContainerDirect.vue) so the two embeddings cannot drift apart.
const props = defineProps<{
    game: APIGameType
    categories: GameCategoriesInfoType | undefined
    gameFrame: GameFrame
    isFullscreen: boolean
    sidebarShown?: boolean
    isTabsOpened: boolean
    showMobileInterface: boolean
    showGameHeader: boolean
    shouldShowLoginPrompt: boolean
    hideBranding?: boolean
    widgetMode?: boolean
    openDisabledAdModal?: (() => Promise<ChargeResponse<DisableAdOrder> | null>) | undefined
}>()

const emit = defineEmits<{
    closeGame: []
    noAdsClick: []
    loginPromptClose: []
    loginPromptLogin: []
    gameFocusChange: [isFocused: boolean]
}>()

const appStore = useAppStore()
const layoutStore = useLayoutStore()
const adDisabledStore = useAdDisabledStore()

const {
    gameSrc,
    isGameFrameVisible,
    adInfo,
    advancedBanners,
    brandPreloaderHasError,
    brandPreloaderAdType,
    isFocusOverlayVisible,
    lastSaveEvent,
    adLoader,
    isDisabledAdFillerVisible,
    disabledAdFillerHandler,
} = props.gameFrame

const gameIframe = useTemplateRef('game-iframe')

const isIOSSafari = computed(() => appStore.platform === 'ios' && !appStore.isAppEnv)

// Build notification queue in priority order: focus > rotate > swipe
const notifierQueue = computed<NotifierItem[]>(() => {
    const queue: NotifierItem[] = []

    if (isDisabledAdFillerVisible.value) {
        return queue
    }

    // Priority 1: Swipe
    if (props.isTabsOpened) {
        queue.push({ type: 'swipe' })
    }

    // Priority 2: Focus
    if (isGameFrameVisible.value && isFocusOverlayVisible.value) {
        queue.push({ type: 'focus' })
    }

    // Priority 3: Rotate
    const supportedOrientations = [props.game.vertical && 'portrait', props.game.horizontal && 'landscape'].filter(
        Boolean,
    ) as Orientation[]

    const shouldShowRotate =
        isGameFrameVisible.value &&
        appStore.platform !== 'desktop' &&
        props.game.source !== 'playhop' &&
        supportedOrientations.length === 1 &&
        layoutStore.screenOrientation &&
        layoutStore.screenOrientation !== supportedOrientations[0]

    if (shouldShowRotate) {
        queue.push({ type: 'rotate', rotateOrientation: supportedOrientations[0] })
    }

    return queue
})

function onErrorAdFillerClose() {
    props.gameFrame.resetErrorPreload()
    focusGame()
}

function syncGameFocus() {
    if (gameIframe.value) {
        emit('gameFocusChange', document.activeElement === gameIframe.value)
    }
}

function syncGameFocusAfterWindowBlur() {
    requestAnimationFrame(syncGameFocus)
}

function focusGame() {
    gameIframe.value?.focus()
    syncGameFocus()
}

function blurGame() {
    gameIframe.value?.blur()
    syncGameFocus()
}

onMounted(() => {
    window.addEventListener('blur', syncGameFocusAfterWindowBlur)
    document.addEventListener('focusin', syncGameFocus)
})

onUnmounted(() => {
    window.removeEventListener('blur', syncGameFocusAfterWindowBlur)
    document.removeEventListener('focusin', syncGameFocus)
})

defineExpose({
    blurGame,
    focusGame,
})
</script>
<style module="$styleCrit" critical>
.game_area {
    position: relative;
    display: grid;
    grid-template-rows: 1fr auto;
    grid-template-areas:
        'main'
        'footer';
    overflow: hidden;
}

.game_area[hidden] {
    display: none;
}

.game_area_with_header {
    grid-template-rows: auto 1fr auto;
    grid-template-areas:
        'header'
        'main'
        'footer';
}

.iframe_wrapper {
    grid-area: main;
}

.game_header_horizontal_override,
.game_header_vertical_override {
    display: none;
    grid-area: header;
}

.game_header_horizontal_override {
    display: flex;
}

.iframe_wrapper {
    position: relative;
}
.iframe,
.iframe_fallback {
    width: 100%;
    height: 100%;
}
.iframe_fallback {
    background-color: var(--black);
    position: absolute;
    top: 0;
    left: 0;
}
.ad_block_override {
    width: 100%;
    height: 50px;
    grid-area: footer;
}

@media (--mobile-landscape) {
    .game_area_with_header:not(.ios_safari) {
        grid-template-rows: 1fr;
        grid-template-columns: auto 1fr;
        grid-template-areas: 'header main';
    }

    .game_area_with_header:not(.ios_safari) .game_header_horizontal_override {
        display: none;
    }
    .game_area_with_header:not(.ios_safari) .game_header_vertical_override {
        display: flex;
    }
}

@media (--tablet-plus) {
    .ad_block_override {
        height: 90px;
    }

    .ad_block_widget_override {
        display: none;
    }
}

@media (--mobile-landscape), (--desktop-small-plus) {
    .ad_block_override {
        display: none;
    }
}
</style>
